/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { DataProvider, IMediaModel } from "@bms/common";
import * as React from "react";
import { WithTranslation } from "react-i18next";

import { LoaderSpinner } from "components/LoaderSpinner";
import { useIsMobile } from "components/Responsive";
import {
  ANALYTICS_EVENTS,
  ANALYTICS_GA_CATEGORIES,
} from "constants/analyticsKeys";
import { useAnalytics } from "context/analytics";

import PlusIcon from "../../resources/icons/plus.svg";
import RemoveIcon from "../../resources/icons/remove.svg";
import { MediaButton } from "../MediaButton";

interface IMyListButtonProps extends WithTranslation {
  media: IMediaModel;
  tabIndex?: number;
}

export const MyListButton = React.memo(
  ({ media, t, tabIndex }: IMyListButtonProps) => {
    const isMobile = useIsMobile();
    const [isOnMyList, setIsOnMyList] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const { trackEvent } = useAnalytics();

    const toggleMyList = () => {
      if (media.Id) {
        setIsLoading(true);
        const wasOnMyList = isOnMyList;
        const togglePromise = wasOnMyList
          ? DataProvider.removeFromMyList(media.Id)
          : DataProvider.addToMyList(media.Id);

        togglePromise
          .then(() => {
            setIsOnMyList(!wasOnMyList);
            trackEvent({
              name: wasOnMyList
                ? ANALYTICS_EVENTS.REMOVE_FROM_WISHLIST
                : ANALYTICS_EVENTS.ADD_TO_WISHLIST,
              gaCategory: ANALYTICS_GA_CATEGORIES.ENGAGEMENT,
              gaLabel: media.Title ?? "",
              ecommerce: {
                items: [{ item_id: media.Id, item_name: media.Title ?? "" }],
              },
            });
          })
          .finally(() => setIsLoading(false));
      }
    };

    React.useEffect(() => {
      if (media.Id) {
        setIsLoading(true);
        DataProvider.isOnMyList(media.Id)
          .then((result) => {
            if (isOnMyList === result) {
              return;
            }
            setIsOnMyList(result);
          })
          .finally(() => setIsLoading(false));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media.Id]);

    const getIcon = () => {
      if (isLoading) {
        return <LoaderSpinner height={20} width={20} />;
      }
      return isOnMyList ? <RemoveIcon /> : <PlusIcon />;
    };

    return (
      <MediaButton
        variant="transparent"
        icon={getIcon()}
        iconElevated={true}
        onClick={toggleMyList}
        ariaLabel={
          isOnMyList
            ? t("COMMON__BUTTON_REMOVE_FROM_MY_LIST")
            : t("COMMON__BUTTON_ADD_TO_MY_LIST")
        }
        tabIndex={tabIndex}
      >
        {!isMobile &&
          (isOnMyList
            ? t("COMMON__BUTTON_REMOVE_FROM_MY_LIST")
            : t("COMMON__BUTTON_ADD_TO_MY_LIST"))}
      </MediaButton>
    );
  },
);
